import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Pharmaceuticals Industry Focus page — its copy and its figures, apart from
 * the component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Pharmaceuticals was one
 * of the nine still carrying it: a two-paragraph series description under an amber
 * note saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını
 * tasarla"*, the same words the Public Services, Retail, Services, Technology and
 * Tourism cuts arrived in — with a four-part full-page capture of digitopia.co's
 * Pharmaceuticals Industry Focus page as the reference, plus four files: the report
 * mockup, Figure 1, and two overlapping crops of the cover. So this is the
 * twenty-seventh designed report page and the twelfth focus cut to arrive with
 * **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiRetail.ts` and
 * `content/daimiPublicServices.ts` do, and `daimiPharmaceuticalsChecks()` feeds them
 * into the same manifest as the rest of the site's amber notes so a production build
 * cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda
 * önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That
 * band is `content/reportClosing.ts`, shared by every report page but three, and it
 * already points at the Digital & AI Maturity Report 2026 — so this page inherits
 * the answer rather than restating it, and the reference capture agrees: its Global
 * Perspective card names the 2026 report too, and names this cut "The
 * Pharmaceuticals Focus Report", which is the `shortName` `focus()` already gives
 * it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These
 * documents are prerendered with no client bundle at all (see `src/prerender.tsx`),
 * so an embedded form script would never run, and the other reports on this site
 * hand the reader the PDF from our own bucket instead. So every Download button here
 * is that PDF, and `DAIMI_PH_DOWNLOAD.check` is what keeps the question of gating
 * open.
 *
 * **Not to be confused with the Future 2030 Pharmaceutical outlook**, which is a
 * separate report with its own slug, its own cover and its own designed page
 * (`content/outlookPharmaceutical.ts`, `pages/OutlookPharmaceutical.tsx`) — which is
 * why the constants here are `DAIMI_PH_*` and that page's are `OUTLOOK_PH_*`. Two
 * reports, two pages, one sector.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working.
 */
export const DAIMI_PH_SLUG = 'digital-and-ai-maturity-report-2026-pharmaceuticals-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 *
 * The 2.84 in the first paragraph is set in bold on the live page. It is plain here,
 * because this page renders its paragraphs as strings rather than as markup — and it
 * is the one number the page can check against itself: 2.84 is also the 2025 average
 * in Figure 1, and the sector sitting just under the 2.90 cross-industry baseline is
 * what the sentence is about.
 */
export const DAIMI_PH_INTRO = {
  body: [
    'The pharmaceuticals sector scores 2.84 in the 2026 DAIMI benchmark, trailing the 2.90 baseline. While the industry possesses world-class scientific capability, maturity is held back by “compliance gravity”: the structural friction created by strict regulation and legacy environments.',
    'To lead in 2026, organizations must overcome these constraints to move AI from experimental pilots into compliant, enterprise-scale production.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply
   * gets a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction, Utilities, Tourism, Technology, Services, Retail and Public
   * Services cuts made.
   *
   * **With the same caveat those covers had, file for file.** It arrived as two
   * captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one cut off at the top through the title's margin. The second is
   * the one held here, because it is the only one that contains the whole design; it
   * is 2000 × 1848 rather than square, so it is missing a sliver of the top margin,
   * and the index card's square crop trims about 4% off each side. `check` asks for
   * the file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-pharmaceuticals-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Pharma Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 2.84 in the first one, which the capture sets in bold and which is also Figure 1’s 2025 average, and the 2.90 baseline beside it, which appears nowhere in Figure 1 and so cannot be checked from the files supplied. The live hero has no cover beside it — the right of the hero is empty but for the ribbon — and the cover is in the hero here because that is what the other designed report pages do; say if the live emptiness was the point. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the fuller one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "Industrializing AI:", "The Talent
 * Differentiator:". Split into `label` and `body` here so the label can be set
 * rather than emphasised inside a sentence, which is what a `<strong>` in the middle
 * of a paragraph gives.
 *
 * Three points, the same count as the Retail cut; the component reads the array, so
 * nothing about the band changes but its length.
 */
export const DAIMI_PH_FINDINGS = {
  heading: 'DAIMI Findings: Controlled Excellence vs. The Scaling Challenge',
  lede: 'Pharmaceutical performance reveals an industry that is quantitatively strong and well-governed but operationally slow to modernize.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in standardized, tightly controlled environments such as manufacturing and quality management.',
    },
    {
      label: 'Industrializing AI',
      body: 'While experimentation is high, the sector faces a “maturity trap” where strong controls often default to caution, delaying the operationalization of AI in regulated workflows.',
    },
    {
      label: 'The Talent Differentiator',
      body: 'Advanced organizations are using upskilling in data literacy to embed analytics directly into daily routines.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request,
   * the same shape the Automotive, Banking, Services and Retail cuts carry rather
   * than the vitality scatter the Technology, Tourism and Public Services cuts do or
   * the portrait score range the Utilities cut does. Six years, 2020 to 2025, each
   * one a range from the laggard through the average to the leader with that year's
   * company count in the header. It is 1200 × 630 on a white ground, so it gets a
   * white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-pharmaceuticals-study-results.webp',
    alt: 'DAIMI study results for the pharmaceuticals industry, 2020 to 2025: the laggard, average and leader score each year, from 1.80–2.40–2.60 in 2020 to 2.00–2.84–3.75 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Pharmaceuticals Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (2 in 2020 rising to 45 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 2.84, is the same number the hero opens with, which is the one cross-check the page can do on itself; note that the spread widens sharply in 2025 (the leader jumps 3.20 to 3.75 while the laggard falls 2.28 to 2.00) and that the average barely moves across the last three years, neither of which the prose mentions. What wants a reading against digitopia.co is that prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_PH_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Pharmaceuticals',
  body: [
    'To win in 2026, pharma executives must move beyond “AI FOMO” and establish repeatable, compliant pathways for enterprise-wide deployment. Success requires modernizing data foundations and adapting governance to enable speed without compromising quality or trust.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn disciplined operations into a sustained digital advantage.',
  ],
  /** The mockup supplied with this request — the pharma cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-pharmaceuticals-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Pharma Focus',
  } satisfies Photo,
  check:
    'Two things here. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And on the live page this band is text only: the right half of that row is empty, and the report mockup you attached does not appear on the page anywhere. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every
 * report page but three — and the reference capture for this page shows exactly
 * that pair, pointing at the Digital & AI Maturity Report 2026, which is the "2024
 * değil 2026" the request asked for. Nothing to change: the shared band already
 * links to `DAIMI_SLUG`, and its opening sentence already names this cut "The
 * Pharmaceuticals Focus Report" — the capture says "The Pharmaceuticals Focus
 * Report" too, which is the `shortName` `focus()` mints.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_PH_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Pharmaceuticals Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiRetailChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures and its cover. Three of the notes are a
 * transcription nobody has read against the original, and one is a decision about
 * gating that belongs to marketing.
 */
export function daimiPharmaceuticalsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_PH_INTRO.check },
    { section: 'findings', check: DAIMI_PH_FINDINGS.check },
    { section: 'analysis', check: DAIMI_PH_ANALYSIS.check },
    { section: 'download', check: DAIMI_PH_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_PH_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
